import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, combineLatestWith } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { RestrictedModeService } from '../../../modules/feature-toggle/services/restricted-mode.service';
import { SubscriptionService } from '../../../modules/feature-toggle/services/subscription.service';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-trial-banner',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      state('void', style({ transform: 'translateY(-100%)', opacity: 0 })),
      state('*', style({ transform: 'translateY(0)', opacity: 1 })),
      transition('void => *', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('* => void', animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  template: `
    <div *ngIf="isVisible" @slideDown
         class="trial-banner"
         role="alert"
         aria-live="polite"
         [class.phase-trial]="phase === 'Trial'"
         [class.phase-grace]="phase === 'GracePeriod'"
         [class.phase-restricted]="phase === 'Restricted'">
      <div class="banner-content">
        <span class="banner-icon">
          <ng-container [ngSwitch]="phase">
            <span *ngSwitchCase="'Trial'">&#9200;</span>
            <span *ngSwitchCase="'GracePeriod'">&#9888;&#65039;</span>
            <span *ngSwitchCase="'Restricted'">&#128274;</span>
          </ng-container>
        </span>
        <span class="banner-text">
          <ng-container [ngSwitch]="phase">
            <span *ngSwitchCase="'Trial'">{{ 'TRIAL_BANNER.TRIAL_ENDING' | translate:{ days: daysUntilNextPhase } }}</span>
            <span *ngSwitchCase="'GracePeriod'">{{ 'TRIAL_BANNER.GRACE_PERIOD' | translate:{ days: daysUntilNextPhase } }}</span>
            <span *ngSwitchCase="'Restricted'">{{ 'TRIAL_BANNER.RESTRICTED' | translate }}</span>
          </ng-container>
        </span>
        <a *ngIf="!isIos" routerLink="/settings/subscription" class="banner-action">
          {{ 'TRIAL_BANNER.SUBSCRIBE_NOW' | translate }}
        </a>
      </div>
    </div>
  `,
  styleUrls: ['./trial-banner.component.css']
})
export class TrialBannerComponent implements OnInit, OnDestroy {
  isVisible = false;
  phase = '';
  daysUntilNextPhase = 0;
  isIos = Capacitor.getPlatform() === 'ios';

  private destroy$ = new Subject<void>();

  constructor(
    private restrictedModeService: RestrictedModeService,
    private subscriptionService: SubscriptionService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.restrictedModeService.shouldShowBanner$.pipe(
      combineLatestWith(
        this.restrictedModeService.subscriptionPhase$,
        this.restrictedModeService.daysUntilNextPhase$
      ),
      takeUntil(this.destroy$)
    ).subscribe(([shouldShow, phase, days]) => {
      // For trial phase, only show banner when 7 or fewer days remain.
      // iOS: never in the trial phase — "Testphase endet in X Tagen" without a way to act in
      // the app invites the App Review question "how do users buy?" (Guideline 3.1.1).
      if (phase === 'Trial' && (days > 7 || this.isIos)) {
        this.isVisible = false;
      } else {
        this.isVisible = shouldShow;
      }
      this.phase = phase;
      this.daysUntilNextPhase = days;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
